import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../lib/api';
import { Check, FileText, Lock, PenLine } from 'lucide-react';
import { LogoMark } from '../components/Logo';
import PdfCanvasViewer from '../components/PdfCanvasViewer';

export default function PublicSign() {
  const { token } = useParams();
  const [signature, setSignature] = useState(null);
  const [signedName, setSignedName] = useState('');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const [filledFields, setFilledFields] = useState({});
  const [intentConfirmed, setIntentConfirmed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [signatureHint, setSignatureHint] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await publicApi.get(`/signatures/public/${token}`);
      setSignature(data.signature);
      setSignedName(data.signature?.display_name || data.signature?.verification_name || data.signature?.signer_name || '');
    } catch (e) {
      const code = e?.response?.data?.error;
      const message = e?.response?.data?.message;
      if (code === 'link_expired' || code === 'link_revoked') {
        setError(message || (code === 'link_expired' ? 'This link has expired.' : 'This link has been revoked and is no longer valid.'));
        return;
      }
      setError('Signing request not found.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setCurrentPage((page) => Math.min(Math.max(page, 1), pageCount || 1));
  }, [pageCount]);

  const startVerification = async () => {
    setWorking(true);
    setError(null);
    try {
      const { data } = await publicApi.post(`/signatures/public/${token}/start-verification`);
      sessionStorage.setItem('idemgo_return_to_sign', token);
      window.location.assign(data.signature.verification_public_url);
    } catch (e) {
      const code = e?.response?.data?.error;
      const message = e?.response?.data?.message;
      if (code === 'link_expired' || code === 'link_revoked') {
        setError(message || (code === 'link_expired' ? 'This link has expired.' : 'This link has been revoked and is no longer valid.'));
        return;
      }
      setError('Could not start KYC verification.');
    } finally {
      setWorking(false);
    }
  };

  const complete = async () => {
    setWorking(true);
    setError(null);
    try {
      const fieldValues = Object.values(filledFields).filter(Boolean);
      const { data } = await publicApi.post(`/signatures/public/${token}/complete`, {
        signed_name: fieldValues[0] || signedName,
      });
      setSignature(data.signature);
    } catch (e) {
      const code = e?.response?.data?.error;
      setError(code === 'kyc_not_approved'
        ? 'KYC verification must be approved before signing.'
        : code === 'link_expired'
          ? 'This link has expired.'
          : code === 'link_revoked'
            ? 'This link has been revoked and is no longer valid.'
            : 'Could not complete the signature.');
    } finally {
      setWorking(false);
    }
  };

  const verificationApproved = signature?.verification_status === 'approved';
  const requiresKyc = signature?.auth_method === 'kyc';
  const canSign = !requiresKyc || verificationApproved;
  const signed = signature?.status === 'signed';
  const setupComplete = !!signature?.setup_complete;
  const blockedReason = signature?.public_blocked_reason;
  const signatureFields = useMemo(() => signature?.signature_fields || [], [signature]);
  const documentUrl = useMemo(
    () => `${publicApi.defaults.baseURL}/signatures/public/${token}/document`,
    [token]
  );
  const requiredSignatureFields = signatureFields.filter((field) => field.required !== false);
  const progressFields = requiredSignatureFields.length > 0 ? requiredSignatureFields : signatureFields;
  const signatureProgressTotal = progressFields.length;
  const signatureProgressFilled = progressFields
    .filter((field) => !!filledFields[signatureFields.indexOf(field)])
    .length;
  const signatureProgressPct = signatureProgressTotal > 0
    ? Math.min(100, Math.max(0, (signatureProgressFilled / signatureProgressTotal) * 100))
    : (signedName.trim() ? 100 : 0);
  const requiredFieldsFilled = signatureFields
    .every((field, index) => field.required === false || !!filledFields[index]);
  const verifiedSignatureName = signature?.display_name || signature?.verification_name || signature?.signer_name || signedName;
  const signingSubtitle = requiresKyc
    ? (verificationApproved
      ? 'Review the document and sign with your verified identity.'
      : 'Complete the required identity verification before you can sign this document.')
    : 'Review the document and sign with your typed name.';

  const formatSignatureType = (value) => {
    const normalized = String(value || '').toLowerCase();
    if (normalized === 'ses') return 'Simple Electronic Signature';
    if (normalized === 'aes') return 'Advanced Electronic Signature';
    return value || 'Electronic Signature';
  };

  const placeSignatureField = (index) => {
    const existingValue = filledFields[index];
    const nextName = (requiresKyc ? verifiedSignatureName : signedName).trim();

    if (!canSign) {
      setActiveField(index);
      setSignatureHint('Complete identity verification before placing your signature.');
      return;
    }

    if (existingValue) {
      setFilledFields((current) => {
        const next = { ...current };
        delete next[index];
        return next;
      });
      setActiveField(null);
      setSignatureHint(null);
      return;
    }

    if (!nextName) {
      setActiveField(index);
      setSignatureHint('Enter your typed name first, then click the highlighted field.');
      return;
    }

    setFilledFields((current) => ({ ...current, [index]: nextName }));
    setActiveField(null);
    setSignatureHint(null);
  };

  const renderSignatureFieldLabel = (field, index) => {
    if (filledFields[index]) {
      return (
        <>
          <span className="text-[10px] uppercase tracking-wider">✓ Signed</span>
          <span className="min-w-0 break-words">{filledFields[index]}</span>
        </>
      );
    }

    return (
      <>
        <span className="text-[10px] uppercase tracking-wider">Signature field</span>
        <span>{field.required === false ? 'Click to sign (optional)' : 'Click to sign'}</span>
      </>
    );
  };

  const renderFieldsOverlay = (page) => (
    <div className="absolute inset-0">
      {signatureFields.map((field, index) => field.page === page ? (
        <button
          type="button"
          key={index}
          onClick={() => placeSignatureField(index)}
          aria-label={filledFields[index] ? `Signature field ${index + 1} filled` : `Click signature field ${index + 1} to sign`}
          className={`absolute group flex cursor-pointer items-center justify-center border-2 border-dashed px-2 py-1 text-center text-[11px] font-mono transition focus:outline-none focus:ring-2 focus:ring-black/20 ${
            filledFields[index]
              ? 'border-green-700 bg-green-100/80 text-green-950 hover:border-green-800 hover:bg-green-200/70 hover:shadow-sm'
              : activeField === index
                ? 'border-black bg-white text-black shadow-sm ring-2 ring-black/15'
                : field.required === false
                  ? 'border-blue-600 bg-blue-100/70 text-blue-950 hover:border-blue-700 hover:bg-blue-200/70 hover:shadow-sm'
                  : 'border-amber-700 bg-amber-100/80 text-amber-950 hover:border-amber-800 hover:bg-amber-200/80 hover:shadow-sm'
          }`}
          style={{
            left: `${field.x * 100}%`,
            top: `${field.y * 100}%`,
            width: `${field.width * 100}%`,
            height: `${field.height * 100}%`,
          }}>
          <span className="min-w-0 flex flex-col items-center gap-0.5 break-words leading-tight">
            {renderSignatureFieldLabel(field, index)}
          </span>
        </button>
      ) : null)}
    </div>
  );

  const renderDocumentPreview = (className = 'mt-5') => (
    <div className={`${className} border border-zinc-200 rounded-sm overflow-hidden bg-white`}>
      <div className="h-11 px-3 border-b border-zinc-200 grid grid-cols-3 items-center gap-3">
        <button
          type="button"
          onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
          disabled={currentPage <= 1}
          className="justify-self-start text-xs font-mono border border-zinc-200 rounded-sm px-2 py-1 disabled:opacity-40"
        >
          Previous
        </button>
        <span className="justify-self-center text-xs font-mono text-zinc-500">page {currentPage} / {pageCount}</span>
        <button
          type="button"
          onClick={() => setCurrentPage((page) => Math.min(page + 1, pageCount))}
          disabled={currentPage >= pageCount}
          className="justify-self-end text-xs font-mono border border-zinc-200 rounded-sm px-2 py-1 disabled:opacity-40"
        >
          Next
        </button>
      </div>
      <PdfCanvasViewer
        pdfUrl={documentUrl}
        currentPage={currentPage}
        onPageCount={setPageCount}
        scrollable={false}
        className="overflow-visible"
      >
        {renderFieldsOverlay(currentPage)}
      </PdfCanvasViewer>
    </div>
  );

  const renderSignaturePlacementCard = ({ locked = false, identityDerived = false } = {}) => (
    <section className="border border-zinc-200 bg-white rounded-sm p-5">
        <h2 className="font-display text-lg font-bold">Signature placement</h2>
        <p className="mt-1 text-xs leading-5 text-zinc-500">
          {locked
            ? 'Complete identity verification before placing your signature.'
            : identityDerived
              ? 'Click the signature field in the PDF preview to place your verified signature.'
              : 'Enter your name, then click the signature field in the PDF preview to place your signature.'}
        </p>

        <div className="mt-5">
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Signature progress</p>
          <div className="mt-2 h-2 bg-zinc-100 rounded-sm overflow-hidden">
            <div
              className="h-full bg-zinc-900 transition-all"
              style={{ width: `${signatureProgressPct}%` }}
            />
          </div>
          <p className="mt-2 text-xs font-mono text-zinc-500">
            {signatureProgressTotal > 0
              ? `${signatureProgressFilled}/${signatureProgressTotal} fields`
              : (signedName.trim() ? 'Ready' : 'Name needed')}
          </p>
        </div>

        {!locked && identityDerived && (
          <div className="mt-5 border border-zinc-200 bg-zinc-50 rounded-sm px-3 py-2.5">
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Verified signature</p>
            <p className="mt-1.5 min-h-5 break-words font-mono text-sm text-zinc-900">
              {verifiedSignatureName || 'Verified identity'}
            </p>
          </div>
        )}

        {!locked && !identityDerived && (
          <>
            <label className="block mt-5 text-sm">
              <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                Typed signature
              </span>
              <input value={signedName} onChange={(e) => {
                setSignedName(e.target.value);
                if (signatureHint) setSignatureHint(null);
              }}
                className="mt-2 w-full border border-zinc-200 rounded-sm px-3 py-3 text-sm font-mono focus:outline-none focus:border-zinc-500"
                placeholder="Your legal name"/>
              {signatureHint && (
                <span className="mt-2 block text-xs leading-5 text-amber-700">
                  {signatureHint}
                </span>
              )}
            </label>

            <div className="mt-5 border border-zinc-200 bg-zinc-50 rounded-sm px-3 py-2.5">
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Signature preview</p>
              <p className="mt-1.5 min-h-5 break-words font-mono text-sm text-zinc-900">
                {signedName.trim() || 'Your typed name'}
              </p>
            </div>
          </>
        )}
      </section>
  );

  const renderDocumentSigningCard = () => (
      <section className="border border-zinc-200 bg-white rounded-sm p-5">
        <h2 className="font-display text-lg font-bold">Document signing</h2>
        <p className="mt-1 text-xs leading-5 text-zinc-500">
          After placing your signature, confirm that you agree to sign this document.
        </p>

        <label className="mt-4 flex items-start gap-3 border border-zinc-200 rounded-sm p-3 text-sm">
          <input
            type="checkbox"
            checked={intentConfirmed}
            disabled={!canSign}
            onChange={(e) => setIntentConfirmed(e.target.checked)}
            className="mt-0.5 disabled:opacity-40"
          />
          <span>I agree to sign this document</span>
        </label>

        {error && <p className="text-sm text-red-700 mt-3">{error}</p>}

        <button onClick={complete} disabled={working || !canSign || !intentConfirmed || (signatureFields.length === 0 ? !signedName.trim() : !requiredFieldsFilled)}
          className="w-full mt-5 bg-black text-white py-3 rounded-sm text-sm font-medium hover:bg-zinc-800 disabled:opacity-40 inline-flex items-center justify-center gap-2">
          <PenLine className="w-4 h-4" strokeWidth={1.5}/>
          {working ? 'Signing...' : 'Confirm and sign'}
        </button>
      </section>
  );

  const renderIdentityVerificationCard = ({ completed = false } = {}) => (
    <section className="border border-zinc-200 bg-white rounded-sm p-5">
      <h2 className="font-display text-lg font-bold">Identity verification</h2>
      <p className="mt-1 text-xs leading-5 text-zinc-500">
        {completed
          ? 'Identity verification has been completed for this document.'
          : 'Complete the required identity verification before you can sign this document.'}
      </p>
      <div className="mt-5">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Verification progress</p>
        <div className="mt-2 h-2 bg-zinc-100 rounded-sm overflow-hidden">
          <div
            className="h-full bg-zinc-900 transition-all"
            style={{ width: completed || verificationApproved ? '100%' : '0%' }}
          />
        </div>
        <p className="mt-2 text-xs font-mono text-zinc-500">
          {completed || verificationApproved ? '1/1 completed' : '0/1 completed'}
        </p>
      </div>
      {!completed && (
        <button onClick={startVerification} disabled={working}
          className="w-full mt-5 bg-black text-white py-3 rounded-sm text-sm font-medium hover:bg-zinc-800 disabled:opacity-50">
          {working ? 'Starting...' : 'Continue identity verification'}
        </button>
      )}
    </section>
  );

  const renderSigningPanel = ({ includeIdentity = false, identityCompleted = false, placementLocked = false, signatureIdentityDerived = false } = {}) => (
    <aside className="space-y-4">
      {includeIdentity && renderIdentityVerificationCard({ completed: identityCompleted })}
      {renderSignaturePlacementCard({ locked: placementLocked, identityDerived: signatureIdentityDerived })}
      {renderDocumentSigningCard()}
    </aside>
  );

  if (loading) return <div className="min-h-screen grid place-items-center text-sm text-zinc-500">Loading...</div>;

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogoMark size={25} className="shrink-0"/>
            <span className="font-display text-lg font-bold">idemGO</span>
            <span className="font-mono text-xs text-zinc-400 hidden sm:inline">/ document signing</span>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500 font-mono"><Lock className="w-3 h-3"/>encrypted</span>
        </div>
      </header>

      <main>
        <div className="max-w-[1200px] mx-auto px-6 py-10 sm:py-12">
        {error && !signature ? (
          <p className="text-sm text-red-700">{error}</p>
        ) : signed ? (
          <div className="text-center w-full max-w-xl mx-auto border border-zinc-200 bg-white rounded-sm p-6 sm:p-8">
            <span className="mx-auto w-12 h-12 grid place-items-center border rounded-sm text-green-700 bg-green-50 border-green-200">
              <Check className="w-5 h-5" strokeWidth={1.5}/>
            </span>
            <h1 className="font-display text-2xl font-bold mt-4">Document signed</h1>
            <p className="text-sm text-zinc-600 mt-2">Your signature has been recorded successfully.</p>
            <div className="mt-6 text-left text-sm border border-zinc-200 rounded-sm p-4 bg-zinc-50">
              <div className="flex items-center gap-2 text-zinc-700 mb-3">
                <FileText className="w-4 h-4 shrink-0" strokeWidth={1.5}/>
                <span className="font-medium min-w-0 break-words">{signature.title}</span>
              </div>
              <div className="flex justify-between py-1 gap-4"><span className="text-zinc-500">Signed name</span><span className="font-mono text-right">{signature.signed_name}</span></div>
              <div className="flex justify-between py-1 gap-4"><span className="text-zinc-500">Signed at</span><span className="font-mono text-right">{new Date(signature.signed_at).toLocaleString()}</span></div>
              <div className="flex justify-between py-1 gap-4"><span className="text-zinc-500">Signature method</span><span className="font-mono text-right">{formatSignatureType(signature.signature_level)}</span></div>
              {(signature.signature_reference || signature.signature_verification_id || signature.verification_id) && (
                <div className="flex justify-between py-1 gap-4"><span className="text-zinc-500">Signature reference</span><span className="font-mono text-right">{signature.signature_reference || signature.signature_verification_id || signature.verification_id}</span></div>
              )}
              <div className="flex justify-between py-1 gap-4"><span className="text-zinc-500">Verification method</span><span className="font-mono text-right">{signature.auth_method === 'kyc' ? 'KYC verification' : 'Email link'}</span></div>
              {signature.kyc_verification_id && (
                <div className="pt-3 mt-3 border-t border-zinc-200">
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Verification ID</div>
                  <div className="font-mono text-xs break-all mt-1">{signature.kyc_verification_id}</div>
                </div>
              )}
            </div>
            {signature.verification_url && (
              <a href={signature.verification_url}
                className="inline-flex justify-center w-full mt-5 border border-zinc-200 py-3 rounded-sm text-sm hover:border-zinc-400">
                Verify signed document
              </a>
            )}
          </div>
        ) : !setupComplete || blockedReason === 'document_not_ready' ? (
          <div className="max-w-2xl border border-zinc-200 bg-white rounded-sm p-6 sm:p-8">
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Document setup</p>
            <h1 className="font-display text-2xl font-bold mt-1">{signature?.title || 'Document signing'}</h1>
            <p className="text-sm text-zinc-600 mt-2">This document is not ready for signing yet.</p>
            <div className="mt-5 border border-amber-300 bg-amber-50 px-3 py-2 rounded-sm text-sm text-amber-900">
              {blockedReason === 'document_not_ready'
                ? 'The sender has not sent this document yet.'
                : 'The sender needs to place at least one signature field before this link can be used.'}
            </div>
          </div>
        ) : requiresKyc && !verificationApproved ? (
          <>
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Identity required</p>
              <h1 className="font-display text-2xl font-bold mt-1 flex items-center gap-2">
                <FileText className="w-5 h-5 shrink-0 text-zinc-500" strokeWidth={1.5}/>
                <span className="min-w-0 break-words">{signature?.title || 'Document signing'}</span>
              </h1>
              <p className="text-sm text-zinc-600 mt-2">Complete the required identity verification before you can sign this document.</p>
            </div>

            <div className="mt-6 grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
              <div className="lg:hidden">
                {renderIdentityVerificationCard()}
              </div>
              <div className="lg:hidden">
                {renderSignaturePlacementCard({ locked: true })}
              </div>
              <div className="min-w-0">
                {renderDocumentPreview('mt-0')}
              </div>
              <div className="lg:hidden">
                {renderDocumentSigningCard()}
              </div>
              <div className="hidden lg:block lg:sticky lg:top-6">
                {renderSigningPanel({ includeIdentity: true, placementLocked: true })}
              </div>
            </div>
          </>
        ) : (
          <>
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Ready to sign</p>
              <h1 className="font-display text-2xl font-bold mt-1 flex items-center gap-2">
                <FileText className="w-5 h-5 shrink-0 text-zinc-500" strokeWidth={1.5}/>
                <span className="min-w-0 break-words">{signature?.title}</span>
              </h1>
              <p className="text-sm text-zinc-600 mt-2">{signingSubtitle}</p>
            </div>

            <div className="mt-6 grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
              <div className="lg:hidden">
                {requiresKyc ? renderIdentityVerificationCard({ completed: true }) : renderSignaturePlacementCard()}
              </div>
              {requiresKyc && (
                <div className="lg:hidden">
                  {renderSignaturePlacementCard({ identityDerived: true })}
                </div>
              )}
              <div className="min-w-0">
                {renderDocumentPreview('mt-0')}
              </div>
              {!requiresKyc && (
                <div className="lg:hidden">
                  {renderDocumentSigningCard()}
                </div>
              )}
              {requiresKyc && (
                <div className="lg:hidden">
                  {renderDocumentSigningCard()}
                </div>
              )}
              <div className="hidden lg:block lg:sticky lg:top-6">
                {renderSigningPanel({
                  includeIdentity: requiresKyc,
                  identityCompleted: requiresKyc,
                  signatureIdentityDerived: requiresKyc,
                })}
              </div>
            </div>
          </>
        )}
        </div>
      </main>
    </div>
  );
}
